import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { GOOGLE_PLAY_URL, APP_STORE_URL } from './MobileAppBanner';
import { Capacitor } from '@capacitor/core';
import authService from '../services/AuthService';
import apiService from '../services/ApiService';
import tutorialService from '../services/TutorialService';
import WelcomeModal from './WelcomeModal';
import { useNavigate } from 'react-router-dom';

const LANGUAGES = [
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'pt', flag: '🇵🇹', label: 'Português' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
];

function SectionLabel({ icon, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
        {label}
      </Typography>
    </Box>
  );
}

const DEFAULT_PREFS = {
  notifGlobal: true,
  notifResults: true,
  notifNewTopic: true,
  notifReminders: true,
};

export default function SettingsModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [savingLang, setSavingLang] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [notifSupported, setNotifSupported] = useState(false);

  const isAuthenticated = authService.isAuthenticated();
  const currentLang = i18n.resolvedLanguage || 'es';

  useEffect(() => {
    setNotifSupported('Notification' in window);
  }, []);

  useEffect(() => {
    if (!open || !isAuthenticated || !notifSupported) return;
    setLoadingPrefs(true);
    apiService.get('/api/v1/notifications/preferences')
      .then((data) => setPrefs(data))
      .catch(() => {})
      .finally(() => setLoadingPrefs(false));
  }, [open, isAuthenticated, notifSupported]);

  const handleLanguageChange = async (langCode) => {
    i18n.changeLanguage(langCode);
    if (!isAuthenticated) return;
    setSavingLang(true);
    try {
      const updatedUser = await apiService.put('/api/v1/users/me/language', { language: langCode });
      authService._persistUser(updatedUser);
      setSnackbar({ open: true, message: t('settings.languageSaved'), severity: 'success' });
    } catch {
      // el cambio local ya se aplicó
    } finally {
      setSavingLang(false);
    }
  };

  const handlePrefChange = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSavingPrefs(true);
    try {
      const saved = await apiService.put('/api/v1/notifications/preferences', updated);
      setPrefs(saved);
    } catch {
      // revertir
      setPrefs(prefs);
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    onClose();
    navigate('/');
  };

  const NOTIF_ITEMS = [
    { key: 'notifGlobal', label: t('settings.notifGlobal') },
    { key: 'notifResults', label: t('settings.notifResults') },
    { key: 'notifNewTopic', label: t('settings.notifNewTopic') },
    { key: 'notifReminders', label: t('settings.notifReminders') },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('settings.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 3 }}>
          {/* ── IDIOMA ── */}
          <SectionLabel icon={<LanguageIcon fontSize="small" />} label={t('settings.sectionLanguage')} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {LANGUAGES.map((lang) => {
              const selected = lang.code === currentLang;
              return (
                <Button
                  key={lang.code}
                  variant={selected ? 'contained' : 'outlined'}
                  size="small"
                  disabled={savingLang}
                  onClick={() => handleLanguageChange(lang.code)}
                  sx={{
                    gap: 0.75,
                    minWidth: 0,
                    px: 1.5,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderRadius: 2,
                  }}
                >
                  <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{lang.flag}</span>
                  {lang.label}
                </Button>
              );
            })}
          </Box>

          {isAuthenticated && notifSupported && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* ── NOTIFICACIONES ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}>
                <Box sx={{ color: 'primary.main', display: 'flex' }}>
                  <NotificationsIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  {t('settings.sectionNotifications')}
                </Typography>
                {(loadingPrefs || savingPrefs) && (
                  <CircularProgress size={14} sx={{ ml: 0.5 }} />
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                {NOTIF_ITEMS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        checked={!!prefs[key]}
                        onChange={(e) => handlePrefChange(key, e.target.checked)}
                        disabled={loadingPrefs || savingPrefs}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                    sx={{ display: 'flex', mb: 0.25 }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* ── APP MÓVIL ── */}
          {!Capacitor.isNativePlatform() && (
            <>
              <Divider sx={{ my: 2 }} />
              <SectionLabel icon={<PhoneIphoneIcon fontSize="small" />} label={t('settings.sectionMobileApp')} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('settings.mobileAppDesc')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  component="a"
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<AndroidIcon />}
                  sx={{ flex: 1, fontSize: '0.78rem' }}
                >
                  Google Play
                </Button>
                <Button
                  component="a"
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneIphoneIcon />}
                  sx={{ flex: 1, fontSize: '0.78rem' }}
                >
                  App Store
                </Button>
              </Box>
            </>
          )}

          {/* ── ACERCA DE ── */}
          <SectionLabel icon={<InfoIcon fontSize="small" />} label={t('settings.sectionAbout')} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('settings.appVersion')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {process.env.REACT_APP_VERSION || '—'}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            onClick={() => { tutorialService.resetTutorial(); setWelcomeOpen(true); }}
            fullWidth
            size="small"
            sx={{ mb: isAuthenticated ? 2 : 0 }}
          >
            {t('settings.openTutorial')}
          </Button>

          {/* ── LOGOUT ── */}
          {isAuthenticated && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              fullWidth
              size="small"
            >
              {t('settings.logout')}
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <WelcomeModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
